import React from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from 'primereact/button';
import FormInputText from '../../components/form/FormInputText';
import { IntegrationSettings } from '../../models/integrationSettings';
import { IntegrationStatus, IntegrationVendor } from '../../models/enums/integrationEnums';
import { parseIntegrationData } from '../../utils/misc-utils';

type ExperianVoieFormValues = {
  accountIdentifier: string;
  accountPassword: string;
  clientId: string;
  clientSecret: string;
  subCode: string;
};

interface EditExperianVoieIntegrationSettingsProps {
  onCancel: () => void;
  onSave?: (settings: IntegrationSettings) => void;
  integrationSettings?: IntegrationSettings;
  environment: string;
}

export const EditExperianVoieIntegrationSettings = ({
  onCancel,
  onSave,
  integrationSettings,
  environment,
}: EditExperianVoieIntegrationSettingsProps): JSX.Element => {
  const isEditMode = !!integrationSettings;

  let accountIdentifier = '';
  let accountPassword = '';
  let clientId = '';
  let clientSecret = '';
  let subCode = '';
  if (isEditMode && integrationSettings?.integrationData) {
    const data = parseIntegrationData(integrationSettings.integrationData);
    accountIdentifier = data.accountIdentifier || '';
    accountPassword = data.accountPassword || '';
    clientId = data.clientId || '';
    clientSecret = data.clientSecret || '';
    subCode = data.subCode || '';
  }

  const validationSchema = yup.object().shape({
    accountIdentifier: yup.string().required('Account Identifier is required'),
    accountPassword: yup.string().required('Account Password is required'),
    clientId: yup.string().required('Client ID is required'),
    clientSecret: yup.string().required('Client Secret is required'),
    subCode: yup.string().required('Sub Code is required'),
  });

  const { control, handleSubmit, formState } = useForm<ExperianVoieFormValues>({
    defaultValues: {
      accountIdentifier: accountIdentifier,
      accountPassword: accountPassword,
      clientId: clientId,
      clientSecret: clientSecret,
      subCode: subCode,
    },
    resolver: yupResolver(validationSchema),
    mode: 'onSubmit',
  });

  const { errors } = formState;

  const onSubmit = (data: ExperianVoieFormValues) => {
    console.log('Form validated successfully:', data);

    if (onSave) {
      let settings: IntegrationSettings;

      if (isEditMode && integrationSettings) {
        settings = {
          ...integrationSettings,
          environment: environment,
          integrationData: JSON.stringify({
            accountIdentifier: data.accountIdentifier,
            accountPassword: data.accountPassword,
            clientId: data.clientId,
            clientSecret: data.clientSecret,
            subCode: data.subCode,
          }),
        };
      } else {
        settings = {
          name: 'Experian VOIE Integration',
          vendor: IntegrationVendor.Experian,
          status: IntegrationStatus.Active,
          environment: environment,
          integrationData: JSON.stringify({
            accountIdentifier: data.accountIdentifier,
            accountPassword: data.accountPassword,
            clientId: data.clientId,
            clientSecret: data.clientSecret,
            subCode: data.subCode,
          }),
        };
      }

      onSave(settings);
    }
  };

  return (
    <div className="surface-section p-4 border-1 surface-border border-round mb-3">
      <h3 className="mt-0 mb-3">{isEditMode ? 'Edit Experian VOIE Settings' : 'Experian VOIE Settings'}</h3>
      <form onSubmit={handleSubmit(onSubmit)} noValidate className="pb-3 p-fluid p-input-filled">
        <div className="field mb-4">
          <FormInputText
            control={control}
            errors={errors}
            field="accountIdentifier"
            label="Account Identifier *"
            placeholder="Enter your Experian Account Identifier"
          />
        </div>

        <div className="field mb-4">
          <FormInputText
            control={control}
            errors={errors}
            field="accountPassword"
            label="Account Password *"
            placeholder="Enter your Experian Account Password"
            type="password"
          />
        </div>

        <div className="field mb-4">
          <FormInputText
            control={control}
            errors={errors}
            field="clientId"
            label="Client ID *"
            placeholder="Enter your Experian Client ID"
          />
        </div>

        <div className="field mb-4">
          <FormInputText
            control={control}
            errors={errors}
            field="clientSecret"
            label="Client Secret *"
            placeholder="Enter your Experian Client Secret"
            type="password"
          />
        </div>

        <div className="field mb-4">
          <FormInputText
            control={control}
            errors={errors}
            field="subCode"
            label="Sub Code *"
            placeholder="Enter your Experian Sub Code"
          />
        </div>

        <div className="flex justify-content-end gap-2 pt-4">
          <Button
            type="button"
            label="Cancel"
            className="p-button-outlined"
            style={{ width: 'auto', minWidth: 'auto' }}
            onClick={onCancel}
          />
          <Button
            type="submit"
            label={isEditMode ? 'Update' : 'Save'}
            className="p-button"
            style={{ width: 'auto', minWidth: 'auto' }}
          />
        </div>
      </form>
    </div>
  );
};
